import { Button, Form, Input, message, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserDetails, makePostCall } from '../../utils/helper';
import './manage-nodes.css';
import { useForm } from 'antd/es/form/Form';
import ErrorBoundary from '../../components/ErrorBoundary';

// Type definitions
interface Unit {
  unit_code: string;
  unit_label: string;
  value?: string;
  label?: string;
}

interface NodeFormData {
  name: string;
  location: string;
  ip: string;
  port: string;
  aet: string;
  operations?: string;
  comments?: string;
}

interface NodePayload extends Omit<NodeFormData, 'port'> {
  user_id: string;
  port: number;
  operations: string;
}

interface LoadingState {
  units: boolean;
  error: string | null;
}

interface APIResponse<T> {
  data?: {
    data?: T;
    success?: boolean;
    message?: string;
  };
  success?: boolean;
  message?: string;
}

interface UserDetails {
  username: string;
}

/**
 * AddNewNode Component
 *
 * A form interface for creating new DICOM modalities/nodes with:
 * - Comprehensive form validation
 * - Real-time error handling
 * - Loading states and user feedback
 * - Responsive design with accessibility features
 * - Secure data submission
 *
 * @component
 * @returns {JSX.Element} The add new node form interface
 */
const AddNewNode: React.FC = () => {
  const [nodeForm] = useForm<NodeFormData>();
  const [unitsList, setUnitsList] = useState<Unit[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    units: false,
    error: null
  });

  /**
   * Fetches the list of available units/locations for the dropdown
   *
   * @async
   * @function getUnitsList
   * @returns {Promise<void>} Promise that resolves when units are loaded
   * @throws {Error} When API call fails or returns invalid data
   */
  const getUnitsList = useCallback(async (): Promise<void> => {
    try {
      setLoadingState(prev => ({ ...prev, units: true, error: null }));

      const res: APIResponse<Unit[]> = await makePostCall('/units-list');
      const units: Unit[] = res.data?.data || [];

      if (!Array.isArray(units)) {
        throw new Error('Invalid units data format');
      }

      setUnitsList(units);
      setLoadingState(prev => ({ ...prev, units: false }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load units';
      console.error('Error fetching units:', error);

      setLoadingState(prev => ({
        ...prev,
        units: false,
        error: errorMessage
      }));

      message.error(`Error loading units: ${errorMessage}`);
    }
  }, []);

  /**
   * Handles form submission for creating a new node
   *
   * Performs comprehensive validation including:
   * - Required field validation
   * - IP address format validation
   * - Port number range validation
   * - AE Title format validation
   * - User authentication validation
   *
   * @async
   * @function onSubmit
   * @returns {Promise<void>} Promise that resolves when node is created
   * @throws {Error} When validation fails or API call fails
   */
  const onSubmit = useCallback(async (): Promise<void> => {
    try {
      // Validate form fields first
      const values: NodeFormData = await nodeForm.validateFields();
      const { ip, port, aet, name, location, comments } = values;

      // Enhanced validation
      if (!name?.trim() || !ip?.trim() || !port || !aet?.trim() || !location) {
        message.error('Missing required fields. Please check the form.');
        return;
      }

      // Validate port number
      const portNumber = parseInt(port, 10);
      if (isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
        message.error('Please enter a valid port number (1-65535)');
        return;
      }

      // Get user details with validation
      const userDetails: UserDetails = getUserDetails();
      if (!userDetails?.username) {
        message.error('User authentication required. Please log in again.');
        return;
      }

      // Sanitize and prepare payload (all nodes are receive/push targets)
      const payload: NodePayload = {
        ip: ip.trim(),
        port: portNumber,
        aet: aet.trim().toUpperCase(),
        name: name.trim(),
        operations: 'push',
        location: location.trim(),
        comments: comments?.trim() || '',
        user_id: userDetails.username,
      };

      setSubmitting(true);

      const res: APIResponse<any> = await makePostCall('/add-modality', payload);

      if (res.data?.success) {
        message.success('Node added successfully');
        nodeForm.resetFields();
      } else {
        throw new Error(res.data?.message || 'Failed to add node');
      }
    } catch (error: any) {
      console.error('Error adding node:', error);

      if (error.errorFields) {
        message.error('Please fix the validation errors in the form');
      } else {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add node';
        message.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  }, [nodeForm]);

  useEffect(() => {
    getUnitsList();
  }, [getUnitsList]);

  /**
   * Memoized options for the units/locations dropdown
   *
   * @function unitsOptions
   * @returns {Array<{label: string, value: string}>} Formatted options array
   */
  const unitsOptions = useMemo(() =>
    unitsList.map(unit => ({
      label: unit.unit_label || unit.label || 'Unknown',
      value: unit.unit_code || unit.value || '',
    })).filter(option => option.value), [unitsList]);

  const isLoading: boolean = submitting || loadingState.units;
  const loadingMessage: string = submitting ? 'Creating node...' : 'Loading data...';

  // Memoized retry handler
  /**
   * Handles retry logic for failed data loading
   *
   * @async
   * @function handleRetry
   * @returns {Promise<void>} Promise that resolves when retry is complete
   */
  const handleRetry = useCallback(async (): Promise<void> => {
    try {
      await getUnitsList();
    } catch (error) {
      console.error('Error during retry:', error);
    }
  }, [getUnitsList]);

  return (
    <ErrorBoundary>
      <Spin spinning={isLoading} tip={loadingMessage}>
        <div className="mn-page">
          {loadingState.error && (
            <div className="error-container">
              <strong>Error:</strong> {loadingState.error}
              <Button
                type="link"
                size="small"
                onClick={handleRetry}
                className="retry-button"
              >
                Retry
              </Button>
            </div>
          )}
          <div className="mn-panel mn-form-panel">
            <div className="mn-panel-head">
              <div className="mn-panel-title">Create New Node</div>
            </div>

            <div className="mn-panel-body">
              <Form
                form={nodeForm}
                onFinish={onSubmit}
                layout="vertical"
                className="mn-form"
              >
                <div className="mn-form-grid">
                  <Form.Item
                    name="name"
                    label="Node Name"
                    rules={[{ required: true, message: 'Please input node name!' }]}
                  >
                    <Input placeholder="Enter node name" />
                  </Form.Item>

                  <Form.Item
                    name="location"
                    label="Location"
                    rules={[{ required: true, message: 'Please select location!' }]}
                  >
                    <Select
                      options={unitsOptions}
                      placeholder="Select a location"
                      loading={loadingState.units}
                    />
                  </Form.Item>

                  <Form.Item
                    name="ip"
                    label="IP / HostName"
                    rules={[
                      { required: true, message: 'Please input IP address!' },
                      {
                        pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                        message: 'Please enter a valid IP address (e.g., 192.168.1.1)'
                      }
                    ]}
                  >
                    <Input placeholder="e.g., 192.168.1.1" />
                  </Form.Item>

                  <Form.Item
                    name="port"
                    label="Port"
                    rules={[
                      { required: true, message: 'Please input port!' },
                      {
                        pattern: /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/,
                        message: 'Please enter a valid port number (1-65535)'
                      }
                    ]}
                  >
                    <Input placeholder="e.g., 8080" type="number" min="1" max="65535" />
                  </Form.Item>

                  <Form.Item
                    name="aet"
                    label="AE Title"
                    className="mn-span-2"
                    rules={[
                      { required: true, message: 'Please input AE title!' },
                      {
                        max: 16,
                        message: 'AE Title must be 16 characters or less'
                      },
                      {
                        pattern: /^[A-Za-z0-9_-]+$/,
                        message: 'AE Title can only contain letters, numbers, underscores, and hyphens'
                      }
                    ]}
                  >
                    <Input placeholder="e.g., OHIF_NODE" maxLength={16} />
                  </Form.Item>

                  <Form.Item name="comments" label="Comments" className="mn-span-2">
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter any additional comments or notes about this node"
                    />
                  </Form.Item>
                </div>

                <div className="mn-form-actions">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="mn-primary-btn"
                    loading={submitting}
                    disabled={isLoading}
                  >
                    {submitting ? 'Creating Node...' : 'Create Node'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Spin>
    </ErrorBoundary>
  );
};

export default AddNewNode;
